import * as _ from "lodash";
import { WB_ProductConditionRow, WB_ProductConditionTypeRow, WB_ProductUOMRow } from "../PA.Core/PAdbSchemaRows";
import { ConditionService } from "../PA.MD.Services/ConditionService";
import { ConditionTypeService } from "../PA.MD.Services/ConditionTypeService";
import {
    Date_Default_Interval_Length,
    Product_Condition_Calced_WbID,
    Product_Condition_Default_SetID,
    Product_Condition_Error_WbID,
    Product_Condition_ListPrice_National,
    Product_Condition_Max_BOM_Depth,
    Product_Hierarchy_Max_Depth,
    Period_Type_Definitions,
    Product_Condition_ListPrice_Customer,
    Product_Hierarchy_Common_Levels,
    Product_Condition_InvoiceTerms,
    Product_Condition_NonTradeLogistics,
    Product_Condition_NonTradeProduct,
    Product_Condition_NonTradeOther,
    Product_Condition_COGS,
    Product_Condition_BasePrice,
    Product_Condition_FundingRate_Base,
    Product_Condition_FundingRate_Kicker,
    Product_Condition_MarkupPct,
    Product_Condition_NetListPrice_Product,
    Product_Condition_GrossListPrice_Product,
} from "./MD.Constants";
import { ProductBOMService } from "../PA.MD.Services/ProductBOMService";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { ProductHierarchyService } from "../PA.MD.Services/ProductHierarchyService";
import { PeriodService } from "../PA.MD.Services/PeriodService";
import { PeriodTypeService } from "../PA.MD.Services/PeriodTypeService";
import { IDateRange } from "../PA.Core/PA.Core.IF.Basic";
import {
    IwbPeriod,
    IwbPeriodType,
    IwbPolicy,
    IwbProductBOM,
    IwbProductCondition,
    IwbProductConditionType,
    IwbProductGroupUOM,
    IwbProductUOM,
} from "../PA.Core/PAdbSchemaRowsIF";
import { UOMService } from "../PA.MD.Services/UOMService";
import { PolicyService } from "../PA.MD.Services/PolicyService";

/**
 * @deprecated This class should not be used
 */
export class PolicyPricingEngine {
    private static async getCndTypeByPolicyReservedTermID(
        wbPolicyIds: number[],
        reservedTerm: string,
        wbPrdTypeId: number,
        wbCustTypeId: number,
    ): Promise<WB_ProductConditionTypeRow> {
        let cndTypeWbId: number = await PolicyService.getConditionTypeWbIDByPolicyReservedTermID(
            wbPolicyIds,
            reservedTerm,
            wbPrdTypeId,
            wbCustTypeId,
        );
        if (!cndTypeWbId) {
            return undefined;
        }

        return await ConditionTypeService.getConditionTypeByWBID(cndTypeWbId);
    }

    //<editor-fold desc="Gross List Price">

    //for all list price methods, the productWbID can be at either the Product or PrdGrp(PPG) level
    static getNationalListPrice(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        customerWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        return new Promise<IwbProductCondition>((resolve, error) => {
            PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
                wbPolicyIDs,
                "GrossListPrice",
                wbPrdTypeID,
                wbCustTypeID,
            )
                .then((cndType) => {
                    if (_.isUndefined(cndType)) return;
                    return PolicyPricingEngine.getConditionUseBOMIfNeeded(
                        cndType,
                        productWbID,
                        effectiveStartDt,
                        effectiveEndDt,
                        customerWbID,
                        undefined,
                        undefined,
                        destUOM,
                    );
                })
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getNationalListPriceList(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        customerWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        if (_.isUndefined(intervalLengthInDays)) {
            intervalLengthInDays = Date_Default_Interval_Length;
        }

        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
                wbPolicyIDs,
                "GrossListPrice",
                wbPrdTypeID,
                wbCustTypeID,
            )
                .then((cndType) => {
                    return PolicyPricingEngine.getConditionListUseBOMIfNeeded(
                        cndType,
                        productWbID,
                        effectiveStartDt,
                        intervalCount,
                        intervalLengthInDays,
                        undefined,
                        undefined,
                        undefined,
                        destUOM,
                    );
                })
                .then((results) => {
                    resolve(results);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getNationalListPriceListByYearWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        segYearPeriodWbID: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
                wbPolicyIDs,
                "GrossListPrice",
                wbPrdTypeID,
                wbCustTypeID,
            )
                .then((cndType) => {
                    if (!_.isUndefined(cndType)) {
                        return PolicyPricingEngine.getConditionMapAggregateForAll(
                            [cndType],
                            productWbID,
                            segYearPeriodWbID,
                            destPeriodTypeWbID,
                            undefined,
                            undefined,
                            undefined,
                            destUOM,
                        );
                    }
                })
                .then((results) => {
                    resolve(results);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    //</editor-fold>

    //<editor-fold desc="Customer Net List Indirect">

    static async getCustomerNetListIndirectPrice(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHierWbId: number,
        custWbID: number,
        prdH_WbPeriodID: number,
        prdHWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        let aCndType: IwbProductConditionType = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_MarkupPct,
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (_.isUndefined(aCndType)) {
            return PolicyPricingEngine.createErrorCndRow("Condition type not found");
        }
        //find the product's parent level for markup Pct
        let prdGrp_MarkUp: number = await ProductHierarchyService.getPrdGrpParent(
            prdHWbID,
            productWbID,
            effectiveStartDt,
            effectiveEndDt,
            Product_Hierarchy_Common_Levels.MarkupPct,
        );
        if (_.isUndefined(prdGrp_MarkUp)) {
            return PolicyPricingEngine.createErrorCndRow(
                "Couldn't find Markup PrdGrp for product WB ID: " + productWbID,
            );
        }

        let cndRow_netList = await PolicyPricingEngine.getCustomerNetListPrice(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            prdHierWbId,
            custWbID,
            prdH_WbPeriodID,
            effectiveStartDt,
            effectiveEndDt,
            destUOM,
        );

        let cndRow_markUpPct: IwbProductCondition = await ConditionService.findCondition(
            aCndType as WB_ProductConditionTypeRow,
            custWbID,
            prdGrp_MarkUp,
            undefined,
            effectiveStartDt,
            effectiveEndDt,
        );
        let retVal = PolicyPricingEngine.createCalcdCndRow(
            productWbID,
            DateUtils.convertFromSAPBW(cndRow_markUpPct.EFFECTIVE_START_DT),
            DateUtils.convertFromSAPBW(cndRow_markUpPct.EFFECTIVE_END_DT),
            custWbID,
        );

        if (!_.isUndefined(cndRow_netList)) {
            if (!_.isUndefined(cndRow_markUpPct)) {
                retVal.VALUE_RATEAMOUNT = cndRow_netList.VALUE_RATEAMOUNT * (1 + cndRow_markUpPct.VALUE_RATEAMOUNT);
            } else {
                retVal.VALUE_RATEAMOUNT = cndRow_netList.VALUE_RATEAMOUNT;
            }
        }

        return retVal;
    }

    static async getCustomerNetListIndirectPriceListByPeriodWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHierWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        segYearPeriodWbID: number,
        periodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        if (_.isUndefined(periodTypeWbID)) {
            let aPeriodType: IwbPeriodType = await PeriodTypeService.getPeriodTypeByTypeID(
                Period_Type_Definitions.DefaultCalcLevel,
            );
            periodTypeWbID = aPeriodType.WB_PERIODM_ID;
        }

        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createCalcdCndMapByPrntPeriodWbID(
                productWbID,
                segYearPeriodWbID,
                custWbID,
                undefined,
                periodTypeWbID,
            );
        let aCndType: IwbProductConditionType = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_MarkupPct,
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (_.isUndefined(aCndType)) {
            return retVal;
        }

        //find the product's parent level for markup Pct
        let prdGrp_MarkUp: number = await ProductHierarchyService.getPrdGrpParentByPeriodWbID(
            prdHierWbID,
            productWbID,
            prdHYearPeriodWbID,
            Product_Hierarchy_Common_Levels.MarkupPct,
        );
        if (_.isUndefined(prdGrp_MarkUp)) {
            return retVal;
        }

        let cndMap_MarkupPct: { [periodWbID: number]: IwbProductCondition } =
            await ConditionService.getCndMapByPeriodLevelUsingPeriodWbID(
                aCndType as WB_ProductConditionTypeRow,
                custWbID,
                prdGrp_MarkUp,
                undefined,
                segYearPeriodWbID,
                periodTypeWbID,
            );
        let cndMap_NetList = await PolicyPricingEngine.getCustomerNetListPriceListByPeriodWbID(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            prdHierWbID,
            custWbID,
            prdHYearPeriodWbID,
            segYearPeriodWbID,
            periodTypeWbID,
            destUOM,
        );

        let numKey: number;
        _.forEach(_.keys(retVal), (aKey) => {
            numKey = +aKey;

            switch (true) {
                case !(numKey in cndMap_NetList) || _.isUndefined(cndMap_NetList[numKey].VALUE_RATEAMOUNT):
                    retVal[numKey] = PolicyPricingEngine.createErrorCndRow("Net list condition not found");
                    break;
                case !(numKey in cndMap_MarkupPct) || _.isUndefined(cndMap_MarkupPct[numKey].VALUE_RATEAMOUNT):
                    //assume if markup pct isn't found, then we just use netlist on its own
                    retVal[numKey].VALUE_RATEAMOUNT = cndMap_NetList[numKey].VALUE_RATEAMOUNT;
                    break;
                default:
                    retVal[numKey].VALUE_RATEAMOUNT =
                        cndMap_NetList[numKey].VALUE_RATEAMOUNT * (1 + cndMap_MarkupPct[numKey].VALUE_RATEAMOUNT);
                    break;
            }
        });

        return retVal;
    }

    //</editor-fold>

    //<editor-fold desc="Customer List Price">

    static async getCustomerListPrice(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHierWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        fundTypeWbID?: number,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        let aCnd: IwbProductCondition = await PolicyPricingEngine.getCustomerListPriceWithDiscounts(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            prdHierWbID,
            prdHYearPeriodWbID,
            effectiveStartDt,
            effectiveEndDt,
            custWbID,
            fundTypeWbID,
            [],
        );

        if (_.isUndefined(aCnd)) return undefined;

        if (!_.isUndefined(destUOM)) {
            await PolicyPricingEngine.applyUOMtoCondition(aCnd, destUOM);
        }

        return aCnd;
    }

    static async getCustomerNetListPrice(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHierWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        let cndTypeWbIDs: number[] = await PolicyPricingEngine.getNetListPriceConditionIDs(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
        );

        let cndTypes: IwbProductConditionType[] = [];
        if (!_.isEmpty(cndTypeWbIDs)) cndTypes = await ConditionTypeService.getConditionTypesByWbIDs(cndTypeWbIDs);
        return await PolicyPricingEngine.getCustomerListPriceWithDiscounts(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            prdHierWbID,
            prdHYearPeriodWbID,
            effectiveStartDt,
            effectiveEndDt,
            custWbID,
            undefined,
            cndTypes,
            destUOM,
        );
    }

    static async getCustomerListPriceWithDiscounts(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHierWbID: number,
        prdHYearPeriodWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        custWbID?: number,
        fundTypeWbID?: number,
        discountCndTypes?: IwbProductConditionType[],
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        return await PolicyPricingEngine.getCustListPriceWithDiscounts(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            prdHYearPeriodWbID,
            prdHierWbID,
            effectiveStartDt,
            effectiveEndDt,
            custWbID,
            fundTypeWbID,
            discountCndTypes,
            destUOM,
        );
    }

    private static async getCustListPriceWithDiscounts(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHYearPeriodWbID: number,
        prdHierWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        custWbID?: number,
        fundTypeWbID?: number,
        discountCndTypes?: IwbProductConditionType[],
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        let cndRow_List: IwbProductCondition;
        let cndTypeCustomerList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            "CustomerListPrice",
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (!_.isEmpty(cndTypeCustomerList)) {
            cndRow_List = await ConditionService.findCondition(
                cndTypeCustomerList,
                custWbID,
                productWbID,
                fundTypeWbID,
                effectiveStartDt,
                effectiveEndDt,
            );
        }

        //if there isn't a customer-specific list price, then find the national list price
        if (_.isUndefined(cndRow_List)) {
            cndRow_List = await PolicyPricingEngine.getNationalListPrice(
                wbPolicyIDs,
                wbPrdTypeID,
                wbCustTypeID,
                productWbID,
                custWbID,
                effectiveStartDt,
                effectiveEndDt,
            );
        }

        if (_.isUndefined(cndRow_List)) {
            return PolicyPricingEngine.createErrorCndRow("Unable to determine list price: " + productWbID);
        }

        //apply UOM
        if (!_.isUndefined(destUOM)) {
            await PolicyPricingEngine.applyUOMtoCondition(cndRow_List, destUOM);
        }

        if (_.isEmpty(discountCndTypes)) {
            return cndRow_List;
        }

        let prdGrp_StructureDiscount = await ProductHierarchyService.getPrdGrpParentByPeriodWbID(
            prdHierWbID,
            productWbID,
            prdHYearPeriodWbID,
            Product_Hierarchy_Common_Levels.PlanLevel,
        );

        if (_.isUndefined(prdGrp_StructureDiscount)) {
            return PolicyPricingEngine.createErrorCndRow("Planning level not found for Product: " + productWbID);
        }

        let cndAggrDiscount = await PolicyPricingEngine.getConditionAggregateForAll(
            discountCndTypes,
            productWbID,
            effectiveStartDt,
            effectiveEndDt,
            custWbID,
            fundTypeWbID,
            undefined,
            destUOM,
        );
        let retVal_CustList: IwbProductCondition = PolicyPricingEngine.createCalcdCndRow(
            productWbID,
            effectiveStartDt,
            effectiveEndDt,
            custWbID,
            fundTypeWbID,
        );
        retVal_CustList.VALUE_RATEAMOUNT = Math.max(
            cndRow_List.VALUE_RATEAMOUNT * (1.0 - cndAggrDiscount.VALUE_RATEAMOUNT),
            0.0,
        ); //this shouldn't go negative for any reason

        return retVal_CustList;
    }

    static async getCustomerListPriceListByInterval(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        return await PolicyPricingEngine.getCustListPriceWithDiscountsListByInterval(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            custWbID,
            prdHYearPeriodWbID,
            prdHWbID,
            effectiveStartDt,
            intervalCount,
            intervalLengthInDays,
            undefined,
            destUOM,
        );
    }

    static async getCustomerListPriceListByYearWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        segYearPeriodWbID: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        //just call the method to get list with discounts without passing discount list
        return await PolicyPricingEngine.getCustListPriceWithDiscountsListByPeriodWbID(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            prdHWbID,
            custWbID,
            prdHYearPeriodWbID,
            prdHWbID,
            segYearPeriodWbID,
            undefined,
            destUOM,
        );
    }

    static async getGrossListPriceListByYearWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        segYearPeriodWbID: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        //just call the method to get list with discounts without passing discount list
        return await PolicyPricingEngine.getGrossListPriceByPeriodWbID(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            custWbID,
            prdHYearPeriodWbID,
            prdHWbID,
            segYearPeriodWbID,
            undefined,
            destUOM,
        );
    }

    static async getProductGrossListPriceByYearWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHWbID: number,
        custWbId: number,
        prdHYearPeriodWbID: number,
        segYearPeriodWbID: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        return await PolicyPricingEngine.getProductGrossListPriceByPeriodWbID(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            custWbId,
            prdHYearPeriodWbID,
            prdHWbID,
            segYearPeriodWbID,
            destPeriodTypeWbID,
            destUOM,
        );
    }

    static async getProductListPriceByDate(
        priceType: string,
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        custWbId: number,
        startDate: Date,
        destUOM?: string,
    ): Promise<number> {
        let retVal: WB_ProductConditionRow = undefined;
        let cndType_ShipperList = undefined;
        if (priceType == "GrossList")
            cndType_ShipperList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
                wbPolicyIDs,
                Product_Condition_GrossListPrice_Product,
                wbPrdTypeID,
                wbCustTypeID,
            );
        else
            cndType_ShipperList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
                wbPolicyIDs,
                Product_Condition_NetListPrice_Product,
                wbPrdTypeID,
                wbCustTypeID,
            );

        if (!cndType_ShipperList) {
            console.log("Cannot find product gross list price type in Condition Type table");
            return undefined;
        }
        let cndRows_ShipperList: WB_ProductConditionRow = await ConditionService.findCondition(
            cndType_ShipperList,
            custWbId,
            productWbID,
            undefined,
            startDate,
            startDate,
        );
        if (priceType == "GrossList" && _.isEmpty(cndRows_ShipperList)) {
            return undefined;
        }

        if (priceType != "GrossList" && _.isEmpty(cndRows_ShipperList)) {
            cndType_ShipperList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
                wbPolicyIDs,
                Product_Condition_GrossListPrice_Product,
                wbPrdTypeID,
                wbCustTypeID,
            );
            cndRows_ShipperList = await ConditionService.findCondition(
                cndType_ShipperList,
                custWbId,
                productWbID,
                undefined,
                startDate,
                startDate,
            );
        }

        if (_.isEmpty(cndRows_ShipperList)) return undefined;

        retVal = cndRows_ShipperList;
        if (destUOM) {
            let prdUOMList: WB_ProductUOMRow[] = await UOMService.getProductUOMsByProductWbIDUOMId(
                productWbID,
                destUOM,
            );
            let aPrdUOMRow: WB_ProductUOMRow = _.find(prdUOMList, (aUOM: WB_ProductUOMRow) => {
                return DateUtils.RangeDatesOverlap(
                    DateUtils.convertFromSAPBW(aUOM.cif.EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(aUOM.cif.EFFECTIVE_END_DT),
                    startDate,
                    startDate,
                );
            });
            let aCnvrsnValue = !_.isUndefined(aPrdUOMRow.cif.CONVERSION_FACTOR)
                ? aPrdUOMRow.cif.CONVERSION_FACTOR
                : undefined;

            if (!_.isUndefined(aCnvrsnValue)) {
                retVal.cif.VALUE_RATEAMOUNT = retVal.cif.VALUE_RATEAMOUNT * aCnvrsnValue;
            }
        }
        return retVal.cif.VALUE_RATEAMOUNT;
    }

    static async getProductNetListPriceByYearWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHWbID: number,
        custWbId: number,
        prdHYearPeriodWbID: number,
        segYearPeriodWbID: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        return await PolicyPricingEngine.getProductNetListPriceByPeriodWbID(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            custWbId,
            prdHYearPeriodWbID,
            prdHWbID,
            segYearPeriodWbID,
            destPeriodTypeWbID,
            destUOM,
        );
    }

    static async getCustomerNetListPriceListByInterval(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHierWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let cndIDs: number[] = await PolicyPricingEngine.getNetListPriceConditionIDs(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
        );
        let cndTypes: IwbProductConditionType[] = await ConditionTypeService.getConditionTypesByWbIDs(cndIDs);
        return await PolicyPricingEngine.getCustListPriceWithDiscountsListByInterval(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            custWbID,
            prdHYearPeriodWbID,
            prdHierWbID,
            effectiveStartDt,
            intervalCount,
            intervalLengthInDays,
            cndTypes,
            destUOM,
        );
    }

    static async getCustomerNetListPriceListByPeriodWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHierWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        segPeriodWbID: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        let cndTypeIDList: number[] = await PolicyPricingEngine.getNetListPriceConditionIDs(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
        );
        let cndTypes: IwbProductConditionType[] = await ConditionTypeService.getConditionTypesByWbIDs(cndTypeIDList);
        return await PolicyPricingEngine.getCustListPriceWithDiscountsListByPeriodWbID(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            productWbID,
            custWbID,
            prdHYearPeriodWbID,
            prdHierWbID,
            segPeriodWbID,
            destPeriodTypeWbID,
            cndTypes,
            destUOM,
        );
    }

    private static async getCustListPriceWithDiscountsListByInterval(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        prdHWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        discountCndTypes?: IwbProductConditionType[],
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        if (_.isUndefined(intervalLengthInDays)) {
            intervalLengthInDays = Date_Default_Interval_Length;
        }

        //try to get the customer list price first. if that doesn't exist, then get the national list price
        let effectiveEndDt: Date = DateUtils.addDays(effectiveStartDt, intervalLengthInDays * intervalCount);
        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createCalcdCndMapByInterval(
                productWbID,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                custWbID,
                undefined,
            );

        let cndType_CustomerList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            "CustomerListPrice",
            wbPrdTypeID,
            wbCustTypeID,
        );
        let cndRows_CustomerList: IwbProductCondition[] = await ConditionService.findConditionsOverDateRange(
            cndType_CustomerList,
            custWbID,
            productWbID,
            undefined,
            effectiveStartDt,
            effectiveEndDt,
        );
        if (!_.isEmpty(cndRows_CustomerList)) {
            PolicyPricingEngine.setCndMapByCndRows(retVal, cndRows_CustomerList);
        }

        let cndType_GrossList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            "GrossListPrice",
            wbPrdTypeID,
            wbCustTypeID,
        );
        let cndRows_GrossList: IwbProductCondition[] = await ConditionService.findConditionsOverDateRange(
            cndType_GrossList,
            undefined,
            productWbID,
            undefined,
            effectiveStartDt,
            effectiveEndDt,
        );

        if (_.isEmpty(cndRows_CustomerList)) {
            PolicyPricingEngine.setCndMapByCndRows(retVal, cndRows_GrossList);
        }

        if (_.isEmpty(cndRows_GrossList) && _.isEmpty(cndRows_CustomerList)) {
            //if we don't have gross or customer list prices, return
            return retVal;
        }

        if (!_.isUndefined(destUOM)) {
            await PolicyPricingEngine.applyUOMtoConditionMap(retVal, destUOM);
        }

        if (!_.isArray(discountCndTypes) || _.isEmpty(discountCndTypes)) {
            //if we don't have any discounts, return what we have
            return retVal;
        }

        let prdGrp_PlanLevel: number = await ProductHierarchyService.getPrdGrpParentByPeriodWbID(
            prdHWbID,
            productWbID,
            prdHYearPeriodWbID,
            Product_Hierarchy_Common_Levels.PlanLevel,
        );
        if (_.isUndefined(prdGrp_PlanLevel)) {
            return retVal;
        }

        //get the aggregate discounts and add them to the list price
        let discountAgg = await PolicyPricingEngine.getConditionMapAggregateForAllByInterval(
            discountCndTypes,
            prdGrp_PlanLevel,
            effectiveStartDt,
            intervalCount,
            intervalLengthInDays,
            custWbID,
            undefined,
            undefined,
            destUOM,
        );
        let numKey: number;
        for (let aKey of _.keys(retVal)) {
            numKey = parseInt(aKey);
            retVal[numKey].VALUE_RATEAMOUNT = Math.max(
                retVal[numKey].VALUE_RATEAMOUNT * (1 - discountAgg[numKey].VALUE_RATEAMOUNT),
                0.0,
            );
        }

        return retVal;
    }

    private static async getCustListPriceWithDiscountsListByPeriodWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        prdHWbID: number,
        segYearPeriodWbID: number,
        destPeriodTypeWbID?: number,
        discountCndTypes?: IwbProductConditionType[],
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createCalcdCndMapByPrntPeriodWbID(
                productWbID,
                segYearPeriodWbID,
                custWbID,
                undefined,
                destPeriodTypeWbID,
            );

        let cndType_CustomerList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            "CustomerListPrice",
            wbPrdTypeID,
            wbCustTypeID,
        );
        let cndRows_CustomerList: IwbProductCondition[] = await ConditionService.findConditionsByPeriodWbID(
            cndType_CustomerList,
            custWbID,
            productWbID,
            undefined,
            segYearPeriodWbID,
        );
        if (!_.isEmpty(cndRows_CustomerList)) {
            PolicyPricingEngine.setCndMapByCndRows(retVal, cndRows_CustomerList);
        }

        let cndType_GrossList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            "GrossListPrice",
            wbPrdTypeID,
            wbCustTypeID,
        );
        let cndRows_GrossList: IwbProductCondition[] = await ConditionService.findConditionsByPeriodWbID(
            cndType_GrossList,
            custWbID,
            productWbID,
            undefined,
            segYearPeriodWbID,
        );
        if (!_.isEmpty(cndRows_GrossList)) {
            //call method that sets prices only if the customer list price wasn't found
            PolicyPricingEngine.setIfUndefinedCndMapByCndRows(retVal, cndRows_GrossList);
        }

        if (!_.isUndefined(destUOM)) {
            await PolicyPricingEngine.applyUOMtoConditionMap(retVal, destUOM);
        }

        //TODO: set values to empty if we don't have customer/gross list prices

        if (_.isUndefined(discountCndTypes) || _.isEmpty(discountCndTypes)) {
            return retVal;
        }

        //once we've found the list price, find any discounts and apply them
        let prdGrp_PlanLevel: number = await ProductHierarchyService.getPrdGrpParentByPeriodWbID(
            prdHWbID,
            productWbID,
            prdHYearPeriodWbID,
            Product_Hierarchy_Common_Levels.StructureDiscount,
        );
        if (_.isUndefined(prdGrp_PlanLevel)) {
            return retVal;
        }

        let discountAgg = await PolicyPricingEngine.getConditionMapAggregateForAll(
            discountCndTypes,
            prdGrp_PlanLevel,
            segYearPeriodWbID,
            destPeriodTypeWbID,
            custWbID,
            undefined,
            undefined,
            destUOM,
        );

        let numKey: number;
        for (let aKey of _.keys(retVal)) {
            numKey = parseInt(aKey);
            retVal[numKey].VALUE_RATEAMOUNT = Math.max(
                retVal[numKey].VALUE_RATEAMOUNT * (1 - discountAgg[numKey].VALUE_RATEAMOUNT),
                0.0,
            ); //shouldn't ever be negative
        }

        return retVal;
    }

    private static async getGrossListPriceByPeriodWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        prdHWbID: number,
        segYearPeriodWbID: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createCalcdCndMapByPrntPeriodWbID(
                productWbID,
                segYearPeriodWbID,
                custWbID,
                undefined,
                destPeriodTypeWbID,
            );

        let cndType_GrossList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            "GrossListPrice",
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (!cndType_GrossList) {
            PolicyPricingEngine.setAllCndMap(retVal, "Cannot find  gross list price type in Condition Type table");
            return retVal;
        }

        let cndRows_GrossList: IwbProductCondition[] = await ConditionService.findConditionsByPeriodWbID(
            cndType_GrossList,
            custWbID,
            productWbID,
            undefined,
            segYearPeriodWbID,
        );
        if (_.isEmpty(cndRows_GrossList)) {
            return retVal;
        }

        PolicyPricingEngine.setCndMapByCndRows(retVal, cndRows_GrossList);

        if (destUOM) {
            await PolicyPricingEngine.applyUOMtoConditionMap(retVal, destUOM);
        }

        return retVal;
    }

    private static async getProductGrossListPriceByPeriodWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        prdHWbID: number,
        segYearPeriodWbID: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createCalcdCndMapByPrntPeriodWbID(
                productWbID,
                segYearPeriodWbID,
                custWbID,
                undefined,
                destPeriodTypeWbID,
            );

        let cndType_ShipperList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_GrossListPrice_Product,
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (!cndType_ShipperList) {
            PolicyPricingEngine.setAllCndMap(
                retVal,
                "Cannot find product gross list price type in Condition Type table",
            );
            return retVal;
        }

        let cndRows_ShipperList: IwbProductCondition[] = await ConditionService.findConditionsByPeriodWbID(
            cndType_ShipperList,
            custWbID,
            productWbID,
            undefined,
            segYearPeriodWbID,
        );
        if (_.isEmpty(cndRows_ShipperList)) {
            return retVal;
        }

        PolicyPricingEngine.setCndMapByCndRows(retVal, cndRows_ShipperList);

        if (destUOM) {
            await PolicyPricingEngine.applyUOMtoConditionMap(retVal, destUOM);
        }

        return retVal;
    }

    private static async getProductNetListPriceByPeriodWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        prdHWbID: number,
        segYearPeriodWbID: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [p: number]: IwbProductCondition }> {
        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createCalcdCndMapByPrntPeriodWbID(
                productWbID,
                segYearPeriodWbID,
                custWbID,
                undefined,
                destPeriodTypeWbID,
            );

        let cndType_ShipperList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_NetListPrice_Product,
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (!cndType_ShipperList) {
            PolicyPricingEngine.setAllCndMap(retVal, "Cannot find product net list price type in Condition Type table");
            return retVal;
        }

        let cndRows_ShipperList: IwbProductCondition[] = await ConditionService.findConditionsByPeriodWbID(
            cndType_ShipperList,
            custWbID,
            productWbID,
            undefined,
            segYearPeriodWbID,
        );
        if (!_.isEmpty(cndRows_ShipperList)) PolicyPricingEngine.setCndMapByCndRows(retVal, cndRows_ShipperList);

        let cndType_GrossList = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_GrossListPrice_Product,
            wbPrdTypeID,
            wbCustTypeID,
        );
        let cndRows_GrossList: IwbProductCondition[] = await ConditionService.findConditionsByPeriodWbID(
            cndType_GrossList,
            custWbID,
            productWbID,
            undefined,
            segYearPeriodWbID,
        );
        if (!_.isEmpty(cndRows_GrossList)) {
            //call method that sets prices only if the customer list price wasn't found
            PolicyPricingEngine.setIfUndefinedCndMapByCndRows(retVal, cndRows_GrossList);
        }

        if (_.isEmpty(retVal)) {
            return retVal;
        }

        if (destUOM) {
            await PolicyPricingEngine.applyUOMtoConditionMap(retVal, destUOM);
        }

        return retVal;
    }

    //</editor-fold>

    //<editor-fold desc="VCM">

    static async getCustomerVCM(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        prdGrpWbID: number,
        prdHierWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        let retVal: IwbProductCondition;

        //find the customer net price
        let cndRow_CustomerList: IwbProductCondition = await PolicyPricingEngine.getCustomerNetListPrice(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            prdGrpWbID,
            prdHierWbID,
            custWbID,
            prdHYearPeriodWbID,
            effectiveStartDt,
            effectiveEndDt,
            destUOM,
        );

        if (_.isUndefined(cndRow_CustomerList)) {
            return PolicyPricingEngine.createErrorCndRow("Customer list price not found");
        }

        //subtract the cost of goods sold
        let cndType_COGS = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            "COGs",
            wbPrdTypeID,
            wbCustTypeID,
        );

        if (_.isEmpty(cndType_COGS)) return cndRow_CustomerList;

        let cndRow_COGS: IwbProductCondition = await PolicyPricingEngine.getConditionUseBOMIfNeeded(
            cndType_COGS,
            prdGrpWbID,
            effectiveStartDt,
            effectiveEndDt,
            custWbID,
            undefined,
            undefined,
            destUOM,
        );

        if (_.isUndefined(cndRow_COGS)) {
            return cndRow_CustomerList;
        }

        retVal = PolicyPricingEngine.createCalcdCndRow(
            prdGrpWbID,
            effectiveStartDt,
            effectiveEndDt,
            custWbID,
            undefined,
        );
        retVal.VALUE_RATEAMOUNT = Math.max(cndRow_CustomerList.VALUE_RATEAMOUNT - cndRow_COGS.VALUE_RATEAMOUNT, 0.0); //this shouldn't ever be negative

        return retVal;
    }

    static async getCustomerCOGS(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        prdGrpWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        //subtract the cost of goods sold
        let cndType_COGS = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            "COGs",
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (_.isUndefined(cndType_COGS)) return;
        let retVal: IwbProductCondition = await PolicyPricingEngine.getConditionUseBOMIfNeeded(
            cndType_COGS,
            prdGrpWbID,
            effectiveStartDt,
            effectiveEndDt,
            custWbID,
            undefined,
            undefined,
            destUOM,
        );

        return retVal;
    }

    static async getCustomerVCMList(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        prdGrpWbID: number,
        prdHierWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        //find the customer list price cndmap object
        let cndCustomerList = await PolicyPricingEngine.getCustomerListPriceListByInterval(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            prdGrpWbID,
            prdHierWbID,
            custWbID,
            prdHYearPeriodWbID,
            effectiveStartDt,
            intervalCount,
            intervalLengthInDays,
            destUOM,
        );

        if (_.isUndefined(cndCustomerList)) {
            prdHierWbID;
            //TODO: create method to determine if no values were set in the map object and set all the return values to 0 if so
            return {};
        }

        //subtract the COGS
        let cndType_COGS = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            "COGs",
            wbPrdTypeID,
            wbCustTypeID,
        );
        let cndRows_COGS = await PolicyPricingEngine.getConditionListUseBOMIfNeeded(
            cndType_COGS,
            prdGrpWbID,
            effectiveStartDt,
            intervalCount,
            intervalLengthInDays,
            custWbID,
            undefined,
            undefined,
            destUOM,
        );

        let numKey: number;
        for (let aKey of _.keys(cndCustomerList)) {
            numKey = parseInt(aKey);
            cndCustomerList[numKey].VALUE_RATEAMOUNT = Math.max(
                cndCustomerList[numKey].VALUE_RATEAMOUNT - cndRows_COGS[numKey].VALUE_RATEAMOUNT,
                0.0,
            ); //this value should never go negative
        }

        return cndCustomerList;
    }

    static async getCustomerVCMListByPeriodWBID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        prdGrpWbID: number,
        prdHierWbID: number,
        custWbID: number,
        prdHYearPeriodWbID: number,
        periodWbID_Seg: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let cndCustomerList = await PolicyPricingEngine.getCustomerListPriceListByYearWbID(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            prdGrpWbID,
            prdHierWbID,
            custWbID,
            prdHYearPeriodWbID,
            periodWbID_Seg,
            destPeriodTypeWbID,
            destUOM,
        );

        if (_.isUndefined(cndCustomerList)) {
            return {};
        }

        let cndRows_COGS = await PolicyPricingEngine.getCOGSMapByPeriodWbID(
            wbPolicyIDs,
            wbPrdTypeID,
            wbCustTypeID,
            prdGrpWbID,
            custWbID,
            periodWbID_Seg,
            destPeriodTypeWbID,
            destUOM,
        );

        let numKey: number;
        for (let aKey of _.keys(cndCustomerList)) {
            numKey = parseInt(aKey);
            if (
                !_.isUndefined(cndCustomerList[numKey].VALUE_RATEAMOUNT) &&
                !_.isUndefined(cndRows_COGS[numKey].VALUE_RATEAMOUNT)
            ) {
                cndCustomerList[numKey].VALUE_RATEAMOUNT = Math.max(
                    cndCustomerList[numKey].VALUE_RATEAMOUNT - cndRows_COGS[numKey].VALUE_RATEAMOUNT,
                    0.0,
                ); //this value should never go negative
            }
        }

        return cndCustomerList;
    }

    //</editor-fold>

    //<editor-fold desc="Costs">

    static async getProductCostSum(
        cndTypes: IwbProductConditionType[],
        prdGrpWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        let retVal: IwbProductCondition = PolicyPricingEngine.createCalcdCndRow(
            prdGrpWbID,
            effectiveStartDt,
            effectiveEndDt,
            undefined,
            undefined,
        );

        for (let aCndType of cndTypes) {
            let aCnd = await PolicyPricingEngine.getSimpleCost(aCndType, prdGrpWbID, effectiveStartDt, effectiveEndDt);

            if (!_.isUndefined(aCnd)) {
                if (!_.isUndefined(destUOM)) {
                    await PolicyPricingEngine.applyUOMtoCondition(aCnd, destUOM);
                }

                if (_.isUndefined(retVal.VALUE_RATEAMOUNT)) {
                    retVal.VALUE_RATEAMOUNT = 0;
                }
                retVal.VALUE_RATEAMOUNT += aCnd.VALUE_RATEAMOUNT;
                let intersectRange: IDateRange = DateUtils.RangeDatesIntersect(
                    DateUtils.convertFromSAPBW(retVal.EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(retVal.EFFECTIVE_END_DT),
                    DateUtils.convertFromSAPBW(aCnd.EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(aCnd.EFFECTIVE_END_DT),
                );

                retVal.EFFECTIVE_START_DT = DateUtils.convertToSAPBW(intersectRange.StartDt);
                retVal.EFFECTIVE_END_DT = DateUtils.convertToSAPBW(intersectRange.EndDt);
            }
        }

        return retVal;
    }

    static async getSimpleCost(
        cndType: IwbProductConditionType,
        prdGrpWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        if (_.isUndefined(effectiveEndDt)) {
            effectiveEndDt = DateUtils.getMaxDate();
        }

        let aCnd: IwbProductCondition = await ConditionService.findCondition(
            cndType as WB_ProductConditionTypeRow,
            undefined,
            prdGrpWbID,
            undefined,
            effectiveStartDt,
            effectiveEndDt,
        );
        if (!_.isUndefined(destUOM) && !_.isUndefined(aCnd)) {
            PolicyPricingEngine.applyUOMtoCondition(aCnd, destUOM);
        }

        return aCnd;
    }

    static async getSimpleCostList(
        cndType: IwbProductConditionType,
        prdGrpWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let cndResults: IwbProductCondition[] = await ConditionService.findConditionsUsingInterval(
            cndType as WB_ProductConditionTypeRow,
            undefined,
            prdGrpWbID,
            undefined,
            effectiveStartDt,
            intervalCount,
            intervalLengthInDays,
        );
        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createCalcdCndMapByInterval(
                prdGrpWbID,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                undefined,
                undefined,
                undefined,
            );
        if (!_.isEmpty(cndResults)) {
            PolicyPricingEngine.setCndMapByCndRows(retVal, cndResults);
        }
        if (!_.isUndefined(destUOM)) {
            PolicyPricingEngine.applyUOMtoConditionMap(retVal, destUOM);
        }
        return retVal;
    }

    //</editor-fold>

    //<editor-fold desc="Get conditions using BOMs if needed">

    static async getConditionUseBOMIfNeeded(
        aCndType: IwbProductConditionType,
        productWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        custWbID?: number,
        fundTypeWbID?: number,
        sBOMSearchFilter?: string,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        let retVal: IwbProductCondition = await ConditionService.findCondition(
            aCndType as WB_ProductConditionTypeRow,
            custWbID,
            productWbID,
            fundTypeWbID,
            effectiveStartDt,
            effectiveEndDt,
        );

        if (_.isUndefined(retVal)) {
            //if we couldn't find the condition, try to find it using the BOM
            retVal = PolicyPricingEngine.createCalcdCndRow(
                productWbID,
                effectiveStartDt,
                effectiveEndDt,
                custWbID,
                fundTypeWbID,
            );
            await PolicyPricingEngine.getConditionUsingBOM(
                aCndType,
                productWbID,
                effectiveStartDt,
                retVal,
                effectiveEndDt,
                custWbID,
                fundTypeWbID,
                sBOMSearchFilter,
                undefined,
                undefined,
            );
            if (_.isUndefined(retVal)) {
                retVal = PolicyPricingEngine.createErrorCndRow(
                    "No condition for Prd: " + productWbID + " Cust: " + custWbID + " Type: " + aCndType.PRD_CNDM_ID,
                );
                return retVal;
            }
        }

        if (!_.isUndefined(destUOM)) {
            await PolicyPricingEngine.applyUOMtoCondition(retVal, destUOM);
        }

        return retVal;
    }

    private static async getConditionUsingBOM(
        cndType: IwbProductConditionType,
        productWbID: number,
        effectiveStartDt: Date,
        retVal: IwbProductCondition,
        effectiveEndDt?: Date,
        custWbID?: number,
        fundTypeWbID?: number,
        sBOMSearchFilter?: string,
        depth?: number,
        sBOMType?: string,
    ) {
        if (depth == 0) {
            retVal = PolicyPricingEngine.createErrorCndRow("BOM Cache Recursion");
            return;
        } else if (_.isUndefined(depth)) {
            depth = Product_Condition_Max_BOM_Depth;
        }

        let bomComps: IwbProductBOM[] = await ProductBOMService.getBomRowsByBOMWbProdIDDateRange(
            productWbID,
            effectiveStartDt,
            effectiveEndDt,
        );

        if (_.isEmpty(bomComps)) {
            //if there aren't any component products, exit
            //let aProd = await ProductService.getProductByWbID(productWbID);
            retVal = PolicyPricingEngine.createErrorCndRow(
                "Comps of " + productWbID + ") not available for Cnd search",
            );
            return;
        }

        for (let aPrdBOMComponent of bomComps) {
            let aCnd_First = cndType;
            let aCompCondition: IwbProductCondition;

            if (!_.isUndefined(cndType)) {
                aCompCondition = await ConditionService.findCondition(
                    aCnd_First as WB_ProductConditionTypeRow,
                    custWbID,
                    aPrdBOMComponent.WB_PROD_ID_COMP,
                    fundTypeWbID,
                    effectiveStartDt,
                    effectiveEndDt,
                );
            }

            //if we couldn't find the price on the component, check whether BOM recursion will return one
            if (_.isUndefined(aCompCondition)) {
                aCompCondition = new WB_ProductConditionRow(
                    {
                        WB_PROD_ID: aPrdBOMComponent.WB_PROD_ID_COMP,
                        PROD_CNDM_ID: cndType.PRD_CNDM_ID,
                        VALUE_RATEAMOUNT: 0,
                    },
                    {},
                );
                await PolicyPricingEngine.getConditionUsingBOM(
                    cndType,
                    aPrdBOMComponent.WB_PROD_ID_COMP,
                    effectiveStartDt,
                    aCompCondition,
                    effectiveEndDt,
                    custWbID,
                    fundTypeWbID,
                    sBOMSearchFilter,
                    --depth,
                    sBOMType,
                );
            }

            if (_.isUndefined(aCompCondition)) {
                //if the component doesn't have a price, exit
                retVal = PolicyPricingEngine.createErrorCndRow(
                    "Condition search failed for Comp: " + aPrdBOMComponent.WB_PROD_ID_COMP + " in BOM: " + productWbID,
                );
            }

            //otherwise add to the price as we recurse
            retVal.VALUE_RATEAMOUNT += aCompCondition.VALUE_RATEAMOUNT * aPrdBOMComponent.COMPONENT_QTY;
        }
    }

    public static async getConditionListUseBOMIfNeeded(
        aCndType: IwbProductConditionType,
        productWbId: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        custWbID?: number,
        fundTypeWbId?: number,
        sBOMSearchFilter?: string,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let cndMap: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createCalcdCndMapByInterval(
                productWbId,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                custWbID,
                fundTypeWbId,
                undefined,
            );
        let cndRows: IwbProductCondition[] = await ConditionService.findConditionsUsingInterval(
            aCndType,
            custWbID,
            productWbId,
            fundTypeWbId,
            effectiveStartDt,
            intervalCount,
            intervalLengthInDays,
        );
        if (!_.isUndefined(cndRows) && !_.isEmpty(cndRows)) {
            PolicyPricingEngine.setCndMapByCndRows(cndMap, cndRows);
        } else if (!_.isUndefined(cndRows) && _.isEmpty(cndRows)) {
            await PolicyPricingEngine.setConditionMapUsingBOM(
                cndMap,
                aCndType,
                productWbId,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                custWbID,
                fundTypeWbId,
                sBOMSearchFilter,
                undefined,
                undefined,
            );
        }

        PolicyPricingEngine.setCndMapByCndRows(cndMap, cndRows);

        if (!_.isUndefined(destUOM)) {
            await PolicyPricingEngine.applyUOMtoConditionMap(cndMap, destUOM);
        }
        return cndMap;
    }

    private static async setConditionMapUsingBOM(
        retVal: { [periodWbID: number]: IwbProductCondition },
        aCndType: IwbProductConditionType,
        productWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        custWbID?: number,
        fundTypeWbID?: number,
        sBOMSearchFilter?: string,
        depth?: number,
        sBOMType?: string,
    ) {
        if (_.isUndefined(retVal) || _.isEmpty(_.keys(retVal))) {
            retVal = await PolicyPricingEngine.createCalcdCndMapByInterval(
                productWbID,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                custWbID,
                fundTypeWbID,
            );
        }

        if (depth == 0) {
            PolicyPricingEngine.setAllCndMap(retVal, "Recursion in BOM");
            return;
        }

        let bomComps: IwbProductBOM[] = await ProductBOMService.getBOMRowsByBOMWbProdID(productWbID);
        if (_.isEmpty(bomComps)) {
            PolicyPricingEngine.setAllCndMap(retVal, "Components not available");
        }

        let compCndMap: { [periodWbID: number]: IwbProductCondition };

        for (let aBomComp of bomComps) {
            let aCompCndList = await ConditionService.findConditionsUsingInterval(
                aCndType,
                custWbID,
                productWbID,
                fundTypeWbID,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
            );

            if (_.isUndefined(aCompCndList) || _.isEmpty(aCompCndList)) {
                //if we can't find prices for this component product, see if it is a BOM with its own components
                await PolicyPricingEngine.setConditionMapUsingBOM(
                    retVal,
                    aCndType,
                    productWbID,
                    effectiveStartDt,
                    intervalCount,
                    intervalLengthInDays,
                    custWbID,
                    fundTypeWbID,
                    sBOMSearchFilter,
                    depth,
                    sBOMType,
                );
            }

            //create a condition dictionary object to place in the condition rows by periodWbID
            compCndMap = await PolicyPricingEngine.createCalcdCndMapByInterval(
                productWbID,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                custWbID,
                fundTypeWbID,
                undefined,
            );
            PolicyPricingEngine.setCndMapByCndRows(compCndMap, aCompCndList);

            let numKey: number;
            for (let aKey of _.keys(retVal)) {
                numKey = parseInt(aKey);
                retVal[numKey].VALUE_RATEAMOUNT += compCndMap[numKey].VALUE_RATEAMOUNT * aBomComp.COMPONENT_QTY;
            }
        }
    }

    //</editor-fold>

    //<editor-fold desc="Structure Discounts">

    static async getStructureDiscounts(
        wbPolicyID: number,
        cndType: WB_ProductConditionTypeRow,
        productWbID_Material: number,
        prdHWbID: number,
        custWbID: number,
        periodWbID_PrdHYear: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        let prdPrnt = await ProductHierarchyService.getProductParentByPeriodWbID(
            prdHWbID,
            productWbID_Material,
            periodWbID_PrdHYear,
            Product_Hierarchy_Common_Levels.StructureDiscount,
        );
        return await PolicyPricingEngine.getConditionAggregateForAll(
            [cndType],
            productWbID_Material,
            effectiveStartDt,
            effectiveEndDt,
            custWbID,
            undefined,
            prdPrnt,
            destUOM,
        );
    }

    static async getStructureDiscountsList(
        wbPolicyID: number,
        cndTypes: WB_ProductConditionTypeRow[],
        productWbID_Material: number,
        prdHWbID: number,
        custWbID: number,
        periodWbID_PrdHYear: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        if (_.isUndefined(intervalLengthInDays)) {
            intervalLengthInDays = Date_Default_Interval_Length;
        }

        let effectiveEndDt: Date = DateUtils.addDays(effectiveStartDt, intervalCount * intervalLengthInDays);
        let prdPrnt: number = await ProductHierarchyService.getProductParent(
            prdHWbID,
            productWbID_Material,
            effectiveStartDt,
            effectiveEndDt,
            Product_Hierarchy_Common_Levels.StructureDiscount,
        );
        return await PolicyPricingEngine.getConditionMapAggregateForAllByInterval(
            cndTypes,
            productWbID_Material,
            effectiveStartDt,
            intervalCount,
            intervalLengthInDays,
            custWbID,
            undefined,
            prdPrnt,
            destUOM,
        );
    }

    //</editor-fold>

    //<editor-fold desc="COGS">

    static getCOGSListByInterval(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        custWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getCndTypeByPolicyReservedTermID(wbPolicyIDs, "COGs", wbPrdTypeID, wbCustTypeID).then(
                (aCndType) => {
                    if (!_.isUndefined(aCndType)) {
                        PolicyPricingEngine.getConditionMapAggregateForAllByInterval(
                            [aCndType],
                            productWbID,
                            effectiveStartDt,
                            intervalCount,
                            intervalLengthInDays,
                            custWbID,
                            undefined,
                            undefined,
                            destUOM,
                        )
                            .then((aCndMap) => {
                                resolve(aCndMap);
                                return;
                            })
                            .catch((err) => {
                                error(err);
                            });
                    } else {
                        resolve(undefined);
                    }
                },
            );
        });
    }

    static getCOGSMapByPeriodWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        custWbID: number,
        periodWbID: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getCndTypeByPolicyReservedTermID(wbPolicyIDs, "COGs", wbPrdTypeID, wbCustTypeID)
                .then((aCndType) => {
                    PolicyPricingEngine.getConditionMapAggregateForAll(
                        [aCndType],
                        productWbID,
                        periodWbID,
                        destPeriodTypeWbID,
                        custWbID,
                        undefined,
                        undefined,
                        destUOM,
                    )
                        .then((aCndMap) => {
                            resolve(aCndMap);
                        })
                        .catch((err) => {
                            error(err);
                        });
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    //</editor-fold>

    //<editor-fold desc="Invoice allowances">

    static async getInvoiceTermsListByInterval(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHWbID: number,
        custWbID: number,
        periodWbID_PrdHYear: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let aCndType: IwbProductConditionType = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_InvoiceTerms,
            wbPrdTypeID,
            wbCustTypeID,
        );
        let aPrdPrntWbID: number = await ProductHierarchyService.getProductParentByPeriodWbID(
            prdHWbID,
            productWbID,
            periodWbID_PrdHYear,
            Product_Hierarchy_Common_Levels.StructureDiscount,
        );
        let aCndMap: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.getConditionMapAggregateForAllByInterval(
                [aCndType],
                productWbID,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                custWbID,
                undefined,
                aPrdPrntWbID,
                destUOM,
            );
        return aCndMap;
    }

    static async getInvoiceTermsListByPeriodWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        prdHWbID: number,
        custWbID: number,
        periodWbID_PrdHYear: number,
        periodWbID_Seg: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let aCndType: IwbProductConditionType = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_InvoiceTerms,
            wbPrdTypeID,
            wbCustTypeID,
        );
        let aPrdPrntWbID: number = await ProductHierarchyService.getProductParentByPeriodWbID(
            prdHWbID,
            productWbID,
            periodWbID_PrdHYear,
            Product_Hierarchy_Common_Levels.StructureDiscount,
        );
        let aCndMap: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.getConditionMapAggregateForAll(
                [aCndType],
                productWbID,
                periodWbID_Seg,
                undefined,
                custWbID,
                undefined,
                aPrdPrntWbID,
                destUOM,
            );
        return aCndMap;
    }

    //</editor-fold>

    //<editor-fold desc="Non-Trade Allowances">

    static async getNonTradeAllowancesListByInterval(
        wbPolicyID: number,
        productWbID: number,
        prdHWbID: number,
        custWbID: number,
        periodWbID_PrdHYear: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let nonTradeCndTypeIDsList: string[] = PolicyPricingEngine.getNonTradeAllowanceCndTypeIDList();
        let cndTypes: IwbProductConditionType[] = await ConditionTypeService.getConditionTypesByCndTypeIDsSrcID(
            nonTradeCndTypeIDsList,
            Product_Condition_Default_SetID,
        );
        let aPrdPrnt: number = await ProductHierarchyService.getProductParentByPeriodWbID(
            prdHWbID,
            productWbID,
            periodWbID_PrdHYear,
            Product_Hierarchy_Common_Levels.StructureDiscount,
        );
        let aCndMap: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.getConditionMapAggregateForAllByInterval(
                cndTypes,
                productWbID,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                custWbID,
                undefined,
                aPrdPrnt,
                destUOM,
            );
        return aCndMap;
    }

    static async getNonTradeAllowancesListByPeriodWbID(
        wbPolicyID: number,
        productWbID: number,
        prdHWbID: number,
        custWbID: number,
        periodWbID_PrdHYear: number,
        periodWbID_Seg: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let nonTradeCndTypeIDsList: string[] = PolicyPricingEngine.getNonTradeAllowanceCndTypeIDList();
        let cndTypes: IwbProductConditionType[] = await ConditionTypeService.getConditionTypesByCndTypeIDsSrcID(
            nonTradeCndTypeIDsList,
            Product_Condition_Default_SetID,
        );
        let aPrdPrnt: number = await ProductHierarchyService.getProductParentByPeriodWbID(
            prdHWbID,
            productWbID,
            periodWbID_PrdHYear,
            Product_Hierarchy_Common_Levels.StructureDiscount,
        );
        let cndMap: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.getConditionMapAggregateForAll(
                cndTypes,
                productWbID,
                periodWbID_Seg,
                undefined,
                custWbID,
                undefined,
                aPrdPrnt,
                destUOM,
            );
        return cndMap;
    }

    //</editor-fold>

    //<editor-fold desc="In-Store Base Price">

    static async getInStoreBasePriceListByInterval(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        custWbID: number,
        periodWbID_PrdHYear: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let aCndType: IwbProductConditionType = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_BasePrice,
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (!aCndType) {
            return undefined;
        }
        return await PolicyPricingEngine.getConditionMapAggregateForAllByInterval(
            [aCndType],
            productWbID,
            effectiveStartDt,
            intervalCount,
            intervalLengthInDays,
            custWbID,
            undefined,
            undefined,
            destUOM,
        );
    }

    static async getInStoreBasePriceListByPeriodWbID(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
        productWbID: number,
        custWbID: number,
        periodWbID_Seg: number,
        destPeriodTypeWbID?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let aCndType: IwbProductConditionType = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_BasePrice,
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (!aCndType) {
            return undefined;
        }
        return await PolicyPricingEngine.getConditionMapAggregateForAll(
            [aCndType],
            productWbID,
            periodWbID_Seg,
            destPeriodTypeWbID,
            custWbID,
            undefined,
            undefined,
            destUOM,
        );
    }

    //</editor-fold>

    //<editor-fold desc="Funding Rate By Intervals">

    static getProductFundingRateBaseListByInterval(
        productWbID: number,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getFundingRateListByInterval(
                productWbID,
                true,
                custWbID_Funding,
                fundTypeWbID,
                periodWbID_PrdH,
                prdHWbID,
                effectiveStartDt,
                intervalCount,
                "StandardRate",
                intervalLengthInDays,
            )
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getPrdGrpFundingRateBaseListByInterval(
        productWbID: number,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getFundingRateListByInterval(
                productWbID,
                false,
                custWbID_Funding,
                fundTypeWbID,
                periodWbID_PrdH,
                prdHWbID,
                effectiveStartDt,
                intervalCount,
                "StandardRate",
                intervalLengthInDays,
            )
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getProductFundingRateKickerListByInterval(
        productWbID: number,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getFundingRateListByInterval(
                productWbID,
                true,
                custWbID_Funding,
                fundTypeWbID,
                periodWbID_PrdH,
                prdHWbID,
                effectiveStartDt,
                intervalCount,
                "StandardRate",
                intervalLengthInDays,
            )
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getPrdGrpFundingRateKickerListByInterval(
        productWbID: number,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getFundingRateListByInterval(
                productWbID,
                false,
                custWbID_Funding,
                fundTypeWbID,
                periodWbID_PrdH,
                prdHWbID,
                effectiveStartDt,
                intervalCount,
                "StandardRate",
                intervalLengthInDays,
            )
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    private static getFundingRateListByInterval(
        productWbID: number,
        isProductLevel: boolean,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        cndTypeID: string,
        intervalLengthInDays?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            ConditionTypeService.getConditionTypeByCndTypeSrcID(cndTypeID, Product_Condition_Default_SetID)
                .then((aCndType) => {
                    if (_.isUndefined(aCndType)) {
                        resolve({});
                        return;
                    }

                    PolicyPricingEngine.getCndListForIntervalUsingHierarchy(
                        aCndType,
                        productWbID,
                        isProductLevel,
                        periodWbID_PrdH,
                        prdHWbID,
                        effectiveStartDt,
                        intervalCount,
                        intervalLengthInDays,
                        undefined,
                        custWbID_Funding,
                        fundTypeWbID,
                        undefined,
                        undefined,
                    )
                        .then((aCndMap) => {
                            resolve(aCndMap);
                        })
                        .catch((err) => {
                            error(err);
                        });
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    //</editor-fold>

    //<editor-fold desc="Funding Rate by Period WB ID">

    static getProductFundingRateBaseListByPeriodWbID(
        productWbID: number,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        periodWbID_Seg: number,
        prdCndMWbID: number,
        destPeriodTypeWbID?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getFundingRateListByPeriodWbID(
                productWbID,
                true,
                custWbID_Funding,
                fundTypeWbID,
                periodWbID_PrdH,
                prdHWbID,
                periodWbID_Seg,
                prdCndMWbID,
                destPeriodTypeWbID,
            )
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getPrdGrpFundingRateBaseListByPeriodWbID(
        productWbID: number,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        periodWbID_Seg: number,
        prdCndMWbID: number,
        destPeriodTypeWbID?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getFundingRateListByPeriodWbID(
                productWbID,
                false,
                custWbID_Funding,
                fundTypeWbID,
                periodWbID_PrdH,
                prdHWbID,
                periodWbID_Seg,
                prdCndMWbID,
                destPeriodTypeWbID,
            )
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getProductFundingRateKickerListByPeriodWbID(
        productWbID: number,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        periodWbID_Seg: number,
        prdCndMWbID: number,
        destPeriodTypeWbID?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getFundingRateListByPeriodWbID(
                productWbID,
                true,
                custWbID_Funding,
                fundTypeWbID,
                periodWbID_PrdH,
                prdHWbID,
                periodWbID_Seg,
                prdCndMWbID,
                destPeriodTypeWbID,
            )
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getPrdGrpFundingRateKickerListByPeriodWbID(
        productWbID: number,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        periodWbID_Seg: number,
        prdCndMWbID: number,
        destPeriodTypeWbID?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            PolicyPricingEngine.getFundingRateListByPeriodWbID(
                productWbID,
                false,
                custWbID_Funding,
                fundTypeWbID,
                periodWbID_PrdH,
                prdHWbID,
                periodWbID_Seg,
                prdCndMWbID,
                destPeriodTypeWbID,
            )
                .then((result) => {
                    resolve(result);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    private static getFundingRateListByPeriodWbID(
        productWbID: number,
        isProductLevel: boolean,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        periodWbID_Seg: number,
        cndTypeWbID: number,
        destPeriodTypeWbID?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        return new Promise<{ [periodWbID: number]: IwbProductCondition }>((resolve, error) => {
            ConditionTypeService.getConditionTypeByWBID(cndTypeWbID)
                .then((aCndType) => {
                    if (_.isUndefined(aCndType)) {
                        resolve({});
                        return;
                    }
                    return PolicyPricingEngine.getCndListUsingHierarchyByPeriodWBID(
                        aCndType,
                        productWbID,
                        isProductLevel,
                        periodWbID_PrdH,
                        prdHWbID,
                        periodWbID_Seg,
                        destPeriodTypeWbID,
                        custWbID_Funding,
                        fundTypeWbID,
                        undefined,
                        undefined,
                    );
                })
                .then((aCndMap) => {
                    resolve(aCndMap);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    //</editor-fold>

    //<editor-fold desc="Total Funding Rate">

    static getProductFundingRateTotal(
        productWbID: number,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        cndTypeWbID_Std: number,
        cndTypeWbID_Addl: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
    ): Promise<IwbProductCondition> {
        return new Promise<IwbProductCondition>((resolve, error) => {
            PolicyPricingEngine.getFundingRateTotal(
                productWbID,
                true,
                custWbID_Funding,
                fundTypeWbID,
                periodWbID_PrdH,
                prdHWbID,
                cndTypeWbID_Std,
                cndTypeWbID_Addl,
                effectiveStartDt,
                effectiveEndDt,
            )
                .then((aCnd) => {
                    resolve(aCnd);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getPrdGrpFundingRateTotal(
        productWbID: number,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        cndTypeWbID_Std: number,
        cndTypeWbID_Addl: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
    ): Promise<IwbProductCondition> {
        return new Promise<IwbProductCondition>((resolve, error) => {
            PolicyPricingEngine.getFundingRateTotal(
                productWbID,
                false,
                custWbID_Funding,
                fundTypeWbID,
                periodWbID_PrdH,
                prdHWbID,
                cndTypeWbID_Std,
                cndTypeWbID_Addl,
                effectiveStartDt,
                effectiveEndDt,
            )
                .then((aCnd) => {
                    resolve(aCnd);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static async getFundingRateTotal(
        productWbID: number,
        isProductLevel: boolean,
        custWbID_Funding: number,
        fundTypeWbID: number,
        periodWbID_PrdH: number,
        prdHWbID: number,
        cndTypeWbID_Std: number,
        cndTypeWbID_Addl: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
    ): Promise<IwbProductCondition> {
        let retVal: IwbProductCondition;
        if (_.isUndefined(effectiveEndDt)) {
            effectiveEndDt = DateUtils.getMaxDate();
        }

        let cndType_BaseFundRate: WB_ProductConditionTypeRow =
            await ConditionTypeService.getConditionTypeByWBID(cndTypeWbID_Std);
        let cndType_KickerFundRate: WB_ProductConditionTypeRow =
            await ConditionTypeService.getConditionTypeByWBID(cndTypeWbID_Addl);

        let prdPrntWbID: number = productWbID;
        let bBaseRateFound: boolean = false;
        let bKickerRateFound: boolean = false;

        let cndRow_Base: IwbProductCondition;
        let cndRow_Kicker: IwbProductCondition;

        for (let i = 0; i < Product_Hierarchy_Max_Depth; i++) {
            if (!bBaseRateFound) {
                cndRow_Base = await ConditionService.findCondition(
                    cndType_BaseFundRate,
                    custWbID_Funding,
                    prdPrntWbID,
                    fundTypeWbID,
                    effectiveStartDt,
                    effectiveEndDt,
                );
                if (!_.isUndefined(cndRow_Base)) {
                    bBaseRateFound = true;
                }
            }
            if (!bKickerRateFound) {
                cndRow_Kicker = await ConditionService.findCondition(
                    cndType_KickerFundRate,
                    custWbID_Funding,
                    prdPrntWbID,
                    fundTypeWbID,
                    effectiveStartDt,
                    effectiveEndDt,
                );
                if (!_.isUndefined(cndRow_Kicker)) {
                    bKickerRateFound = true;
                }
            }
            if (bBaseRateFound && bKickerRateFound) {
                break;
            }

            if (isProductLevel) {
                prdPrntWbID = await ProductHierarchyService.getProductParent(
                    prdHWbID,
                    prdPrntWbID,
                    effectiveStartDt,
                    effectiveEndDt,
                );
                isProductLevel = false;
            } else {
                prdPrntWbID = await ProductHierarchyService.getPrdGrpParent(
                    prdHWbID,
                    prdPrntWbID,
                    effectiveStartDt,
                    effectiveEndDt,
                );
            }
            if (_.isUndefined(prdPrntWbID)) {
                break;
            }
        }

        if (!(bKickerRateFound || bBaseRateFound)) {
            return PolicyPricingEngine.createErrorCndRow("Condition not found");
        }

        retVal = PolicyPricingEngine.createCalcdCndRow(
            productWbID,
            effectiveStartDt,
            effectiveEndDt,
            custWbID_Funding,
            fundTypeWbID,
        );
        if (_.isUndefined(retVal.VALUE_RATEAMOUNT)) {
            retVal.VALUE_RATEAMOUNT = 0;
        }

        if (!_.isUndefined(cndRow_Base)) {
            retVal.VALUE_RATEAMOUNT += cndRow_Base.VALUE_RATEAMOUNT;
        }
        if (!_.isUndefined(cndRow_Kicker)) {
            retVal.VALUE_RATEAMOUNT += cndRow_Kicker.VALUE_RATEAMOUNT;
        }

        return retVal;
    }

    //</editor-fold>

    //<editor-fold desc="Get conditions walking up hierarchy if necessary">

    //get a map of periodWbIDs to conditions valid during that periodWbID key
    //value in map will be undefined if there is not a valid condition during that period
    private static async getCndListUsingHierarchyByPeriodWBID(
        aCndType: IwbProductConditionType,
        productWbID: number,
        bIsProductLevel: boolean,
        periodWbID_PrdHYear: number,
        prdHWbID: number,
        periodWbID_Seg: number,
        destPeriodTypeWbID?: number,
        custWbID?: number,
        fundTypeWbID?: number,
        prdHierIterations?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        if (_.isUndefined(prdHierIterations)) {
            prdHierIterations = Product_Hierarchy_Max_Depth;
        }

        //create an empty condition map to hold values as we find them when walking up the hierarchy
        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createEmptyCndMapByPrntPeriodWbID(periodWbID_Seg, destPeriodTypeWbID);
        let aPrntPrdGrpWbID: number = productWbID;
        let cndMap: { [periodWbID: number]: IwbProductCondition };

        for (let i = 0; i < prdHierIterations; i++) {
            if (!_.isUndefined(aPrntPrdGrpWbID)) {
                cndMap = await ConditionService.getCndMapByPeriodLevelUsingPeriodWbID(
                    aCndType as WB_ProductConditionTypeRow,
                    custWbID,
                    aPrntPrdGrpWbID,
                    fundTypeWbID,
                    periodWbID_Seg,
                    destPeriodTypeWbID,
                );
            }

            //only map the new conditions if that child period doesn't have a record yet
            PolicyPricingEngine.setCndMapIfUndefinedByCndMap(retVal, cndMap);

            if (bIsProductLevel) {
                aPrntPrdGrpWbID = await ProductHierarchyService.getProductParentByPeriodWbID(
                    prdHWbID,
                    aPrntPrdGrpWbID,
                    periodWbID_PrdHYear,
                );
                bIsProductLevel = false;
            } else {
                aPrntPrdGrpWbID = await ProductHierarchyService.getPrdGrpParentByPeriodWbID(
                    prdHWbID,
                    aPrntPrdGrpWbID,
                    periodWbID_PrdHYear,
                );
            }

            if (_.isUndefined(aPrntPrdGrpWbID)) {
                break;
            }
        }
        if (!_.isUndefined(destUOM)) {
            await PolicyPricingEngine.applyUOMtoConditionMap(retVal, destUOM);
        }

        return retVal;
    }

    private static async getCndListForIntervalUsingHierarchy(
        aCndType: IwbProductConditionType,
        productWbID: number,
        bIsProductLevel: boolean,
        periodWbID_PrdHYear: number,
        prdHWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        periodTypeID?: string,
        custWbID?: number,
        fundTypeWbID?: number,
        prdHierIterations?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        if (_.isUndefined(prdHierIterations)) {
            prdHierIterations = Product_Hierarchy_Max_Depth;
        }
        if (_.isUndefined(periodTypeID)) {
            periodTypeID = Period_Type_Definitions.Week;
        }
        if (_.isUndefined(intervalLengthInDays)) {
            intervalLengthInDays = Date_Default_Interval_Length;
        }

        //create an empty condition map to hold values as we find them when walking up the hierarchy
        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createEmptyCndMapByInterval(
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                periodTypeID,
            );
        let aPrntPrdGrpWbID: number = productWbID;
        let cndMap: { [periodWbID: number]: IwbProductCondition };

        for (let i = 0; i < prdHierIterations; i++) {
            cndMap = await ConditionService.getCndMapByPeriodLevelUsingDateRange(
                aCndType as WB_ProductConditionTypeRow,
                custWbID,
                aPrntPrdGrpWbID,
                fundTypeWbID,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                periodTypeID,
            );

            //only map the new conditions if that child period doesn't have a record yet
            PolicyPricingEngine.setCndMapIfUndefinedByCndMap(retVal, cndMap);

            if (bIsProductLevel) {
                aPrntPrdGrpWbID = await ProductHierarchyService.getProductParentByPeriodWbID(
                    prdHWbID,
                    aPrntPrdGrpWbID,
                    periodWbID_PrdHYear,
                );
                bIsProductLevel = false;
            } else {
                aPrntPrdGrpWbID = await ProductHierarchyService.getPrdGrpParentByPeriodWbID(
                    prdHWbID,
                    aPrntPrdGrpWbID,
                    periodWbID_PrdHYear,
                );
            }

            if (_.isUndefined(aPrntPrdGrpWbID)) {
                break;
            }
        }

        if (!_.isUndefined(destUOM)) {
            await PolicyPricingEngine.applyUOMtoConditionMap(retVal, destUOM); //this will apply the child's UOM, not the parents. is this an issue?
        }

        return retVal;
    }

    //</editor-fold>

    //<editor-fold desc="Helper functions">

    private static createErrorCndRow(errorMessage: string): WB_ProductConditionRow {
        return new WB_ProductConditionRow(
            {
                PROD_CND_ID: Product_Condition_Error_WbID,
                CONDITION_TYPE: errorMessage,
            },

            {},
        );
    }

    private static createBOMCndRow(
        productWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt: Date,
    ): IwbProductCondition {
        return new WB_ProductConditionRow(
            {
                PROD_CND_ID: Product_Condition_Calced_WbID,
                WB_PROD_ID: productWbID,
                EFFECTIVE_START_DT: effectiveStartDt,
                EFFECTIVE_END_DT: effectiveEndDt,
            },
            {},
        );
    }

    private static setAllCndMap(cndMap: { [periodWbID: number]: IwbProductCondition }, errorMessage: string) {
        for (let aKey of _.keys(cndMap)) {
            let errorCndRow = PolicyPricingEngine.createErrorCndRow(errorMessage);
            cndMap[parseInt(aKey)] = errorCndRow;
        }
    }

    private static createCalcdCndRow(
        prdGrpWbID: number,
        effectiveStartDate: Date,
        effectiveEndDate?: Date,
        custWbID?: number,
        fundTypeWbID?: number,
    ): IwbProductCondition {
        return new WB_ProductConditionRow(
            {
                PROD_CND_ID: Product_Condition_Calced_WbID,
                EFFECTIVE_START_DT: DateUtils.convertToSAPBW(effectiveStartDate),
                EFFECTIVE_END_DT: _.isUndefined(effectiveEndDate)
                    ? undefined
                    : DateUtils.convertToSAPBW(effectiveEndDate),
                WB_CUST_ID: custWbID,
                WB_PRDGRP_ID: prdGrpWbID,
                WB_FUNDTYPE_ID: fundTypeWbID,
                VALUE_RATEAMOUNT: undefined,
            },
            {},
        );
    }

    //create a dictionary with one entry per period level falling within startDate + interval*intervalLength, keyed by periodWbID
    //value in dictionary is ProductCondition_Row with effective dates set to entry's period dates
    private static async createCalcdCndMapByInterval(
        prdGrpWbID: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        custWbID?: number,
        fundTypeWbID?: number,
        periodTypeID?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let retVal: { [periodWbID: number]: IwbProductCondition } = {};

        if (_.isUndefined(intervalLengthInDays)) {
            intervalLengthInDays = Date_Default_Interval_Length;
        }
        if (_.isUndefined(periodTypeID)) {
            //assume week by default
            periodTypeID = Period_Type_Definitions.Week;
        }

        let effectiveEndDt: Date = DateUtils.addDays(effectiveStartDt, intervalLengthInDays * intervalCount);

        let periodType: IwbPeriodType = await PeriodTypeService.getPeriodTypeByTypeID(periodTypeID);
        let periodRows: IwbPeriod[] = await PeriodService.getPeriodsByPeriodTypeWbIDDateRange(
            periodType.WB_PERIODM_ID,
            effectiveStartDt,
            effectiveEndDt,
        );

        for (let aPeriodRow of periodRows) {
            retVal[aPeriodRow.WB_PERIOD_ID] = PolicyPricingEngine.createCalcdCndRow(
                prdGrpWbID,
                DateUtils.convertFromSAPBW(aPeriodRow.PERIOD_START_DT),
                DateUtils.convertFromSAPBW(aPeriodRow.PERIOD_END_DT),
                custWbID,
                fundTypeWbID,
            );
        }

        return retVal;
    }

    private static async createCalcdCndMapByPrntPeriodWbID(
        prdGrpWbID: number,
        prntPeriodWbID: number,
        custWbID?: number,
        fundTypeWbID?: number,
        destPeriodTypeWbID?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let retVal: { [periodWbID: number]: IwbProductCondition } = {};

        if (_.isUndefined(destPeriodTypeWbID)) {
            let aPeriodType: IwbPeriodType = await PeriodTypeService.getPeriodTypeByTypeID(
                Period_Type_Definitions.DefaultCalcLevel,
            );
            destPeriodTypeWbID = aPeriodType.WB_PERIODM_ID;
        }

        let periodRows: IwbPeriod[] = await PeriodService.getAllChildrenByWBIDChildTypeWbId(
            destPeriodTypeWbID,
            prntPeriodWbID,
        );

        for (let aPeriodRow of periodRows) {
            retVal[aPeriodRow.WB_PERIOD_ID] = PolicyPricingEngine.createCalcdCndRow(
                prdGrpWbID,
                DateUtils.convertFromSAPBW(aPeriodRow.PERIOD_START_DT),
                DateUtils.convertFromSAPBW(aPeriodRow.PERIOD_END_DT),
                custWbID,
                fundTypeWbID,
            );
        }

        return retVal;
    }

    private static async createEmptyCndMapByPrntPeriodWbID(
        prntPeriodWbID: number,
        destPeriodTypeWbID?: number,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let retVal: { [periodWbID: number]: IwbProductCondition } = {};

        if (_.isUndefined(destPeriodTypeWbID)) {
            let aPeriodType: IwbPeriodType = await PeriodTypeService.getPeriodTypeByTypeID(
                Period_Type_Definitions.DefaultCalcLevel,
            );
            destPeriodTypeWbID = aPeriodType.WB_PERIODM_ID;
        }

        let periodRows: IwbPeriod[] = await PeriodService.getAllChildrenByWBIDChildTypeWbId(
            destPeriodTypeWbID,
            prntPeriodWbID,
        );

        for (let aPeriodRow of periodRows) {
            retVal[aPeriodRow.WB_PERIOD_ID] = undefined;
        }

        return retVal;
    }

    private static async createEmptyCndMapByInterval(
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        periodTypeID?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let retVal: { [periodWbID: number]: IwbProductCondition } = {};

        if (_.isUndefined(intervalLengthInDays)) {
            intervalLengthInDays = Date_Default_Interval_Length;
        }
        if (_.isUndefined(periodTypeID)) {
            //assume week by default
            periodTypeID = Period_Type_Definitions.Week;
        }

        let effectiveEndDt: Date = DateUtils.addDays(effectiveStartDt, intervalLengthInDays * intervalCount);

        let periodType: IwbPeriodType = await PeriodTypeService.getPeriodTypeByTypeID(periodTypeID);
        let periodRows: IwbPeriod[] = await PeriodService.getPeriodsByPeriodTypeWbIDDateRange(
            periodType.WB_PERIODM_ID,
            effectiveStartDt,
            effectiveEndDt,
        );

        for (let aPeriodRow of periodRows) {
            retVal[aPeriodRow.WB_PERIOD_ID] = undefined;
        }

        return retVal;
    }

    private static setCndMapByCndRows(
        cndMap: { [periodWbID: number]: IwbProductCondition },
        cndList: IwbProductCondition[],
    ) {
        let numKey: number;
        let aCndRow: IwbProductCondition;
        for (let aKey of _.keys(cndMap)) {
            numKey = parseInt(aKey);

            aCndRow = _.find(cndList, (aCnd) => {
                return DateUtils.RangeDatesOverlap(
                    DateUtils.convertFromSAPBW(aCnd.EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(aCnd.EFFECTIVE_END_DT),
                    DateUtils.convertFromSAPBW(cndMap[numKey].EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(cndMap[numKey].EFFECTIVE_END_DT),
                );
            });

            if (!_.isUndefined(aCndRow)) {
                cndMap[numKey].VALUE_RATEAMOUNT = aCndRow.VALUE_RATEAMOUNT;
            } else {
                cndMap[numKey].VALUE_RATEAMOUNT = undefined;
            }
        }
    }

    private static setIfUndefinedCndMapByCndRows(
        cndMap: { [periodWbID: number]: IwbProductCondition },
        cndList: IwbProductCondition[],
    ) {
        //only set the values if they are currently blank. use case: product doesn't have customer specific for whole timeframe, populate blanks with gross list price
        let numKey: number;
        let aCndRow: IwbProductCondition;
        for (let aKey of _.keys(cndMap)) {
            numKey = parseInt(aKey);

            aCndRow = _.find(cndList, (aCnd) => {
                return DateUtils.RangeDatesOverlap(
                    DateUtils.convertFromSAPBW(aCnd.EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(aCnd.EFFECTIVE_END_DT),
                    DateUtils.convertFromSAPBW(cndMap[numKey].EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(cndMap[numKey].EFFECTIVE_END_DT),
                );
            });

            if (!_.isUndefined(aCndRow) && _.isUndefined(cndMap[numKey].VALUE_RATEAMOUNT)) {
                cndMap[numKey].VALUE_RATEAMOUNT = aCndRow.VALUE_RATEAMOUNT;
            }
        }
    }

    private static addCndMapByCndRows(
        cndMap: { [periodWbID: number]: IwbProductCondition },
        cndList: IwbProductCondition[],
    ) {
        let numKey: number;
        let aCndRow: IwbProductCondition;
        for (let aKey of _.keys(cndMap)) {
            numKey = parseInt(aKey);

            aCndRow = _.find(cndList, (aCnd) => {
                return DateUtils.RangeDatesOverlap(
                    DateUtils.convertFromSAPBW(aCnd.EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(aCnd.EFFECTIVE_END_DT),
                    DateUtils.convertFromSAPBW(cndMap[numKey].EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(cndMap[numKey].EFFECTIVE_END_DT),
                );
            });

            if (!_.isUndefined(aCndRow)) {
                if (_.isUndefined(cndMap[numKey].VALUE_RATEAMOUNT)) {
                    cndMap[numKey].VALUE_RATEAMOUNT = 0;
                } //calc'd records are initialized to undefined, so have to set to 0 before adding
                cndMap[numKey].VALUE_RATEAMOUNT += aCndRow.VALUE_RATEAMOUNT;
            }
        }
    }

    private static async applyUOMtoCondition(targetCnd: IwbProductCondition, uomType: string): Promise<void> {
        if (targetCnd.VALUE_UOM != uomType) {
            if (!_.isUndefined(targetCnd.WB_PROD_ID)) {
                //if the WB_Prod_ID is defined, then assume we're looking for the Product-level UOM. Otherwise look for PrdGrp level UOM
                let aUOM: IwbProductUOM = await UOMService.getProductUOMByProductWbIDDateRange(
                    targetCnd.WB_PROD_ID,
                    uomType,
                    DateUtils.convertFromSAPBW(targetCnd.EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(targetCnd.EFFECTIVE_END_DT),
                );

                if (!_.isUndefined(aUOM)) {
                    targetCnd.VALUE_RATEAMOUNT = targetCnd.VALUE_RATEAMOUNT * aUOM.CONVERSION_FACTOR;
                }
            } else {
                let aPrdGrpUOM: IwbProductGroupUOM = await UOMService.getPrdGrpUOMByPrdGrpWbIDDateRange(
                    targetCnd.WB_PRDGRP_ID,
                    uomType,
                    DateUtils.convertFromSAPBW(targetCnd.EFFECTIVE_START_DT),
                    DateUtils.convertFromSAPBW(targetCnd.EFFECTIVE_END_DT),
                );
                if (!_.isUndefined(aPrdGrpUOM)) {
                    targetCnd.VALUE_RATEAMOUNT = targetCnd.VALUE_RATEAMOUNT * aPrdGrpUOM.CONVERSION_FACTOR;
                }
            }
        }
    }

    private static async applyUOMtoConditionMap(
        targetCndMap: { [periodWbID: number]: IwbProductCondition },
        uomType: string,
    ): Promise<void> {
        //using one method to check for both product and product group level UOMs. if product UOM isn't found, then look for product group UOM
        let numKey: number;
        let aPrdUOMRow: IwbProductUOM;
        let aPrdGrpUOMRow: IwbProductGroupUOM;
        let aCnvrsnValue: number;
        let prdUOMList: IwbProductUOM[];
        let prdGrpUOMList: IwbProductGroupUOM[];
        let productWbID: number;

        //since the pricing engine can have multiple IwbProductConditions in one targetCndMap (valid in different periods), the product level/UOM values may differ between periods. Check for each
        for (let aKey of _.keys(targetCndMap)) {
            numKey = parseInt(aKey);
            if (targetCndMap[numKey].VALUE_UOM == uomType) {
                continue;
            } //if condition is already in target UOM, skip

            if (!_.isUndefined(targetCndMap[numKey].WB_PROD_ID)) {
                //if the WB_Prod_ID is defined, then assume we're looking for the Product-level UOM. Otherwise look for PrdGrp level UOM
                prdUOMList = await UOMService.getProductUOMsByProductWbIDUOMId(
                    targetCndMap[numKey].WB_PROD_ID,
                    uomType,
                );
                aPrdUOMRow = _.find(prdUOMList, (aUOM: IwbProductUOM) => {
                    return DateUtils.RangeDatesOverlap(
                        DateUtils.convertFromSAPBW(aUOM.EFFECTIVE_START_DT),
                        DateUtils.convertFromSAPBW(aUOM.EFFECTIVE_END_DT),
                        DateUtils.convertFromSAPBW(targetCndMap[numKey].EFFECTIVE_START_DT),
                        DateUtils.convertFromSAPBW(targetCndMap[numKey].EFFECTIVE_END_DT),
                    );
                });
                aCnvrsnValue = !_.isUndefined(aPrdUOMRow.CONVERSION_FACTOR) ? aPrdUOMRow.CONVERSION_FACTOR : undefined;
            } else {
                prdGrpUOMList = await UOMService.getPrdGrpUOMsByPrdGrpWbIDUOMId(
                    targetCndMap[numKey].WB_PRDGRP_ID,
                    uomType,
                );
                if (_.isArray(prdGrpUOMList) && !_.isEmpty(prdGrpUOMList)) {
                    aPrdGrpUOMRow = _.find(prdGrpUOMList, (aUOM: IwbProductGroupUOM) => {
                        return DateUtils.RangeDatesOverlap(
                            DateUtils.convertFromSAPBW(aUOM.EFFECTIVE_START_DT),
                            DateUtils.convertFromSAPBW(aUOM.EFFECTIVE_END_DT),
                            DateUtils.convertFromSAPBW(targetCndMap[numKey].EFFECTIVE_START_DT),
                            DateUtils.convertFromSAPBW(targetCndMap[numKey].EFFECTIVE_END_DT),
                        );
                    });

                    aCnvrsnValue = !_.isUndefined(aPrdGrpUOMRow) ? aPrdGrpUOMRow.CONVERSION_FACTOR : undefined;
                }
            }

            if (!_.isUndefined(aCnvrsnValue)) {
                targetCndMap[numKey].VALUE_RATEAMOUNT = targetCndMap[numKey].VALUE_RATEAMOUNT * aCnvrsnValue;
            }
        }

        return;
    }

    private static setCndMapIfUndefinedByCndMap(
        retVal: { [periodWbID: number]: IwbProductCondition },
        cndList: { [periodWbID: number]: IwbProductCondition },
    ) {
        let numKey: number;
        for (let aKey of _.keys(retVal)) {
            numKey = parseInt(aKey);
            if (_.isUndefined(retVal[numKey])) {
                retVal[numKey] = cndList[numKey];
            }
        }
    }

    private static async getNetListPriceConditionIDs(
        wbPolicyIDs: number[],
        wbPrdTypeID: number,
        wbCustTypeID: number,
    ): Promise<number[]> {
        let cndTypeIdList: number[] = [];
        let cnd: IwbProductConditionType = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_InvoiceTerms,
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (!_.isUndefined(cnd)) {
            cndTypeIdList.push(cnd.PRD_CNDM_ID);
        }
        cnd = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_NonTradeLogistics,
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (!_.isUndefined(cnd)) {
            cndTypeIdList.push(cnd.PRD_CNDM_ID);
        }
        cnd = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_NonTradeProduct,
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (!_.isUndefined(cnd)) {
            cndTypeIdList.push(cnd.PRD_CNDM_ID);
        }
        cnd = await PolicyPricingEngine.getCndTypeByPolicyReservedTermID(
            wbPolicyIDs,
            Product_Condition_NonTradeOther,
            wbPrdTypeID,
            wbCustTypeID,
        );
        if (!_.isUndefined(cnd)) {
            cndTypeIdList.push(cnd.PRD_CNDM_ID);
        }
        return cndTypeIdList;
    }

    private static getNonTradeAllowanceCndTypeIDList(): string[] {
        let retVal: string[] = [];
        retVal.push(Product_Condition_NonTradeLogistics);
        retVal.push(Product_Condition_NonTradeProduct);
        retVal.push(Product_Condition_NonTradeOther);
        return retVal;
    }

    private static async getConditionAggregateForAll(
        cndTypes: IwbProductConditionType[],
        productWbIDPrimary: number,
        effectiveStartDt: Date,
        effectiveEndDt?: Date,
        custWbID?: number,
        fundTypeWbID?: number,
        productWbIDSecondary?: number,
        destUOM?: string,
    ): Promise<IwbProductCondition> {
        let retVal: IwbProductCondition = PolicyPricingEngine.createCalcdCndRow(
            productWbIDPrimary,
            effectiveStartDt,
            effectiveEndDt,
            custWbID,
            fundTypeWbID,
        );

        if (_.isUndefined(effectiveEndDt)) {
            effectiveEndDt = DateUtils.getMaxDate();
        }

        let aCnd: IwbProductCondition;
        for (let aCndType of cndTypes) {
            aCnd = await ConditionService.findCondition(
                aCndType as WB_ProductConditionTypeRow,
                custWbID,
                productWbIDPrimary,
                fundTypeWbID,
                effectiveStartDt,
                effectiveEndDt,
            );
            if (_.isUndefined(aCnd) && !_.isUndefined(productWbIDSecondary)) {
                aCnd = await ConditionService.findCondition(
                    aCndType as WB_ProductConditionTypeRow,
                    custWbID,
                    productWbIDSecondary,
                    fundTypeWbID,
                    effectiveStartDt,
                    effectiveEndDt,
                );
            }

            if (!_.isUndefined(destUOM)) {
                await PolicyPricingEngine.applyUOMtoCondition(retVal, destUOM);
            }

            if (!_.isUndefined(aCnd)) {
                retVal.VALUE_RATEAMOUNT += aCnd.VALUE_RATEAMOUNT;
            }
        }

        return retVal;
    }

    private static async getConditionMapAggregateForAll(
        cndTypes: IwbProductConditionType[],
        productWbIDPrimary: number,
        parentPeriodWbID: number,
        destPeriodTypeWbID?: number,
        custWbID?: number,
        fundTypeWbID?: number,
        productWbIDSecondary?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createCalcdCndMapByPrntPeriodWbID(
                productWbIDPrimary,
                parentPeriodWbID,
                custWbID,
                fundTypeWbID,
                destPeriodTypeWbID,
            );

        for (let aCndType of cndTypes) {
            let cndList: WB_ProductConditionRow[] = await ConditionService.findConditionsByPeriodWbID(
                aCndType as WB_ProductConditionTypeRow,
                custWbID,
                productWbIDPrimary,
                fundTypeWbID,
                parentPeriodWbID,
            );

            if ((_.isUndefined(cndList) || _.isEmpty(cndList)) && !_.isUndefined(productWbIDSecondary)) {
                cndList = await ConditionService.findConditionsByPeriodWbID(
                    aCndType as WB_ProductConditionTypeRow,
                    custWbID,
                    productWbIDPrimary,
                    fundTypeWbID,
                    parentPeriodWbID,
                );
            }

            PolicyPricingEngine.addCndMapByCndRows(retVal, cndList);
            if (!_.isUndefined(destUOM)) {
                await PolicyPricingEngine.applyUOMtoConditionMap(retVal, destUOM);
            }
        }

        return retVal;
    }

    private static async getConditionMapAggregateForAllByInterval(
        cndTypes: IwbProductConditionType[],
        productWbIDPrimary: number,
        effectiveStartDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
        custWbID?: number,
        fundTypeWbID?: number,
        productWbIDSecondary?: number,
        destUOM?: string,
    ): Promise<{ [periodWbID: number]: IwbProductCondition }> {
        if (_.isUndefined(intervalLengthInDays)) {
            intervalLengthInDays = Date_Default_Interval_Length;
        }

        let retVal: { [periodWbID: number]: IwbProductCondition } =
            await PolicyPricingEngine.createCalcdCndMapByInterval(
                productWbIDPrimary,
                effectiveStartDt,
                intervalCount,
                intervalLengthInDays,
                custWbID,
                fundTypeWbID,
            );
        let effectiveEndDt: Date = DateUtils.addDays(effectiveStartDt, intervalLengthInDays * intervalCount);

        for (let aCndType of cndTypes) {
            let cndList = await ConditionService.findConditionsOverDateRange(
                aCndType as WB_ProductConditionTypeRow,
                custWbID,
                productWbIDPrimary,
                fundTypeWbID,
                effectiveStartDt,
                effectiveEndDt,
            );

            if (_.isUndefined(cndList) || (_.isEmpty(cndList) && !_.isUndefined(productWbIDSecondary))) {
                cndList = await ConditionService.findConditionsOverDateRange(
                    aCndType as WB_ProductConditionTypeRow,
                    custWbID,
                    productWbIDSecondary,
                    fundTypeWbID,
                    effectiveStartDt,
                    effectiveEndDt,
                );
            }

            if (!_.isUndefined(destUOM)) {
                await PolicyPricingEngine.applyUOMtoConditionMap(retVal, destUOM);
            }
            PolicyPricingEngine.addCndMapByCndRows(retVal, cndList);
        }

        return retVal;
    }

    //</editor-fold>
}
